"use client";

import { c as _c } from "react/compiler-runtime";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { CompactMenu, type CompactMenuItem } from "@/components/compact-menu";
import { useAuth } from "@/components/auth/Auth";
import MyStreamWaveCurationCreateDialog from "@/components/brain/my-stream/tabs/MyStreamWaveCurationCreateDialog";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import CommonConfirmationModal from "@/components/utils/modal/CommonConfirmationModal";
import type { ApiGroup } from "@/generated/models/ApiGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import WaveGroupScope from "@/components/waves/specs/groups/group/WaveGroupScope";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import type { DropCurationMembership } from "@/hooks/drops/useDropCurations";
import { getWaveCurationsQueryKey, useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { commonApiDelete, commonApiFetch } from "@/services/api/common-api";
const toScopeGroup = (group: ApiGroupFull): ApiGroup => ({
  id: group.id,
  name: group.name,
  author: group.created_by,
  is_hidden: !group.visible,
  is_direct_message: group.is_direct_message ?? false
});
export default function WaveActiveCurationSection(t0) {
  const $ = _c(72);
  const {
    wave
  } = t0;
  const {
    isApp
  } = useDeviceInfo();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("curation");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeCurationId = t1;
  const canManageCurations = wave.wave.authenticated_user_eligible_for_admin === true;
  const shouldLoadCurations = isApp || activeCurationId !== null;
  let t2;
  if ($[2] !== shouldLoadCurations || $[3] !== wave.id) {
    t2 = {
      waveId: wave.id,
      enabled: shouldLoadCurations
    };
    $[2] = shouldLoadCurations;
    $[3] = wave.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3
  } = useWaveCurations(t2);
  let t4;
  if ($[5] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const curations = t4;
  const resolvedActiveCurationId = activeCurationId ?? (isApp ? curations[0]?.id ?? null : null);
  let t5;
  if ($[7] !== curations || $[8] !== resolvedActiveCurationId) {
    t5 = curations.find(curation => curation.id === resolvedActiveCurationId) ?? null;
    $[7] = curations;
    $[8] = resolvedActiveCurationId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const activeCuration = t5;
  const activeGroupId = activeCuration?.group_id ?? null;
  let t6;
  let t7;
  if ($[10] !== activeGroupId) {
    t6 = [QueryKey.GROUP, activeGroupId];
    t7 = async () => {
      if (!activeGroupId) {
        throw new Error("No active group selected.");
      }
      return await commonApiFetch({
        endpoint: `groups/${activeGroupId}`
      });
    };
    $[10] = activeGroupId;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  const t8 = !!activeGroupId;
  let t9;
  if ($[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      enabled: t8,
      staleTime: 300000
    };
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    data: activeGroup,
    isFetching: isFetchingActiveGroup
  } = useQuery(t9);
  let t10;
  if ($[17] !== pathname || $[18] !== router || $[19] !== searchParams) {
    t10 = curationId => {
      const params = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params.set("curation", curationId);
      } else {
        params.delete("curation");
      }
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[17] = pathname;
    $[18] = router;
    $[19] = searchParams;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const setSelectedCuration = t10;
  let t11;
  if ($[21] !== activeCuration || $[22] !== requestAuth || $[23] !== wave.id) {
    t11 = async () => {
      if (!activeCuration) {
        throw new Error("No active curation selected.");
      }
      const auth = await requestAuth();
      if (!auth.success) {
        throw new Error("Authentication was cancelled.");
      }
      await commonApiDelete({
        endpoint: `waves/${wave.id}/curations/${activeCuration.id}`
      });
    };
    $[21] = activeCuration;
    $[22] = requestAuth;
    $[23] = wave.id;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== activeCuration || $[26] !== queryClient || $[27] !== setSelectedCuration || $[28] !== setToast || $[29] !== wave.id) {
    t12 = async () => {
      if (!activeCuration) {
        return;
      }
      queryClient.setQueryData(getWaveCurationsQueryKey(wave.id), current => current?.filter(item => item.id !== activeCuration.id) ?? current);
      queryClient.setQueriesData({
        queryKey: ["drop-curations"]
      }, current_0 => current_0?.filter(item_0 => item_0.id !== activeCuration.id) ?? current_0);
      await queryClient.invalidateQueries({
        queryKey: getWaveCurationsQueryKey(wave.id)
      });
      await queryClient.invalidateQueries({
        queryKey: ["drop-curations"]
      });
      setToast({
        type: "success",
        message: "Curation deleted."
      });
      setIsDeleteOpen(false);
      setSelectedCuration(null);
    };
    $[25] = activeCuration;
    $[26] = queryClient;
    $[27] = setSelectedCuration;
    $[28] = setToast;
    $[29] = wave.id;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== setToast) {
    t13 = error => {
      const message = error instanceof Error ? error.message : "Failed to delete curation.";
      setToast({
        type: "error",
        message
      });
    };
    $[31] = setToast;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13) {
    t14 = {
      mutationFn: t11,
      onSuccess: t12,
      onError: t13
    };
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const deleteMutation = useMutation(t14);
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsEditOpen(true);
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = !activeGroup;
  let t17;
  if ($[38] !== t16) {
    t17 = {
      id: "edit",
      label: "Edit curation",
      onSelect: t15,
      disabled: t16
    };
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      id: "delete",
      label: "Delete curation",
      onSelect: () => setIsDeleteOpen(true),
      className: "tw-text-red desktop-hover:hover:tw-text-red"
    };
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17) {
    t19 = [t17, t18];
    $[41] = t17;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const menuItems = t19;
  if (!isApp && (!activeCurationId || !activeCuration)) {
    return null;
  }
  const desktopActiveCuration = isApp ? null : activeCuration;
  let t20;
  if ($[43] !== activeCuration?.group_id || $[44] !== activeGroup || $[45] !== canManageCurations || $[46] !== curations || $[47] !== desktopActiveCuration?.group_id || $[48] !== desktopActiveCuration?.name || $[49] !== isApp || $[50] !== isFetchingActiveGroup || $[51] !== menuItems || $[52] !== resolvedActiveCurationId || $[53] !== setSelectedCuration) {
    t20 = isApp ? curations.length > 0 && <div className="tw-pb-4"><div className="tw-flex tw-items-start tw-justify-between tw-gap-x-6 tw-px-4 tw-pt-4"><p className="tw-mb-0 tw-text-base tw-font-semibold tw-tracking-tight tw-text-iron-200">Curation</p></div><div className="tw-mt-2 tw-flex tw-flex-col tw-gap-y-1 tw-px-4">{curations.map(curation_0 => {
          const isActive = curation_0.id === resolvedActiveCurationId;
          return <div key={curation_0.id} className={clsx("tw-rounded-xl tw-border tw-border-solid tw-transition-colors tw-duration-200", isActive ? "tw-border-iron-800 tw-bg-iron-950/70" : "tw-border-transparent")}><div className="tw-flex tw-items-center"><button type="button" onClick={() => setSelectedCuration(curation_0.id)} className={clsx("tw-flex tw-h-10 tw-min-w-0 tw-flex-1 tw-items-center tw-rounded-xl tw-border-0 tw-bg-transparent tw-px-4 tw-text-left tw-transition-colors tw-duration-200", isActive ? "tw-text-iron-50" : "tw-text-iron-300 desktop-hover:hover:tw-bg-iron-900/55 desktop-hover:hover:tw-text-iron-100")} aria-pressed={isActive}><span className={clsx("tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-leading-tight", isActive ? "tw-font-semibold" : "tw-font-medium")}>{curation_0.name}</span></button>{isActive && canManageCurations && <CompactMenu triggerClassName="tw-mr-1 tw-flex tw-size-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-100" trigger={<EllipsisHorizontalIcon className="tw-size-5" />} aria-label="Active curation options" items={menuItems} menuWidthClassName="tw-w-44" />}</div>{isActive && <div className="tw-flex tw-min-h-6 tw-items-center tw-justify-between tw-gap-x-4 tw-pb-2.5 tw-pl-4 tw-pr-4 tw-text-sm"><span className="tw-font-medium tw-text-iron-500">Group</span>{activeGroup ? <WaveGroupScope group={toScopeGroup(activeGroup)} /> : <span className="tw-max-w-40 tw-truncate tw-text-sm tw-font-medium tw-text-iron-200">{isFetchingActiveGroup ? "Loading..." : activeCuration?.group_id}</span>}</div>}</div>;
        })}</div></div> : <div className="tw-pb-4"><div className="tw-flex tw-items-start tw-justify-between tw-gap-x-6 tw-px-4 tw-pt-4"><div className="tw-min-w-0"><p className="tw-mb-1 tw-text-base tw-font-semibold tw-tracking-tight tw-text-iron-200">Curation</p><p className="tw-mb-0 tw-truncate tw-text-sm tw-font-medium tw-text-iron-400">{desktopActiveCuration?.name}</p></div>{canManageCurations && <CompactMenu triggerClassName="tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-100" trigger={<EllipsisHorizontalIcon className="tw-size-5" />} aria-label="Active curation options" items={menuItems} menuWidthClassName="tw-w-44" />}</div><div className="tw-mt-2 tw-flex tw-flex-col tw-gap-y-2 tw-px-4"><div className="tw-flex tw-h-6 tw-items-center tw-justify-between tw-text-sm"><span className="tw-font-medium tw-text-iron-500">Group</span>{activeGroup ? <WaveGroupScope group={toScopeGroup(activeGroup)} /> : <span className="tw-max-w-40 tw-truncate tw-text-sm tw-font-medium tw-text-iron-200">{isFetchingActiveGroup ? "Loading..." : desktopActiveCuration?.group_id}</span>}</div></div></div>;
    $[43] = activeCuration?.group_id;
    $[44] = activeGroup;
    $[45] = canManageCurations;
    $[46] = curations;
    $[47] = desktopActiveCuration?.group_id;
    $[48] = desktopActiveCuration?.name;
    $[49] = isApp;
    $[50] = isFetchingActiveGroup;
    $[51] = menuItems;
    $[52] = resolvedActiveCurationId;
    $[53] = setSelectedCuration;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== activeCuration || $[56] !== activeGroup || $[57] !== isEditOpen || $[58] !== wave) {
    t21 = isEditOpen && activeCuration && activeGroup && <MyStreamWaveCurationCreateDialog key={`${activeCuration.id}:${activeGroup.id}`} wave={wave} isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} onSaved={_temp} curation={activeCuration} initialGroup={activeGroup} />;
    $[55] = activeCuration;
    $[56] = activeGroup;
    $[57] = isEditOpen;
    $[58] = wave;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setIsDeleteOpen(false);
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== deleteMutation) {
    t23 = () => deleteMutation.mutate();
    $[61] = deleteMutation;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  const t24 = activeCuration ? `Delete "${activeCuration.name}" from this wave?` : "Delete this curation from this wave?";
  let t25;
  if ($[63] !== deleteMutation.isPending || $[64] !== isDeleteOpen || $[65] !== t23 || $[66] !== t24) {
    t25 = <CommonConfirmationModal isOpen={isDeleteOpen} onClose={t22} onConfirm={t23} title="Delete curation" message={t24} confirmText="Delete" isConfirming={deleteMutation.isPending} />;
    $[63] = deleteMutation.isPending;
    $[64] = isDeleteOpen;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t20 || $[69] !== t21 || $[70] !== t25) {
    t26 = <>{t20}{t21}{t25}</>;
    $[68] = t20;
    $[69] = t21;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  return t26;
}
function _temp() {}